import { Link } from 'react-router-dom';
import { DIAGNOSTIC_QUESTION_COUNT, INDEPENDENCE_DISCLAIMER } from '@titrate/shared';
import { useExams } from '../lib/queries';
import { usePageMeta } from '../lib/pageMeta';

export function Landing() {
  const { data } = useExams();
  // No arguments restores the site default, which is already this page's —
  // called explicitly so returning here from /pricing or /about is correct
  // too, not just a fresh load.
  usePageMeta();

  return (
    <div className="space-y-12 sm:space-y-16">
      <section className="pt-6 text-center">
        <p className="inline-flex rounded-full bg-brand-50 dark:bg-brand-500/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-800 dark:text-brand-300">
          For overseas-qualified pharmacists
        </p>
        <h1 className="mx-auto mt-5 max-w-3xl text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
          Find out exactly where you stand before you sit CAOP or OPRA
        </h1>
        <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-muted sm:text-lg">
          Start with a free {DIAGNOSTIC_QUESTION_COUNT}-question diagnostic, weighted exactly like the real
          paper. In about half an hour you will know which content domains are costing you marks and
          whether your pace would survive the real thing.
        </p>
        <div className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center">
          <Link to="/register" className="btn-primary px-6 py-3 text-base">
            Take the free diagnostic
          </Link>
          <Link to="/pricing" className="btn-secondary px-6 py-3 text-base">
            See pricing
          </Link>
        </div>
        <p className="mt-4 text-sm text-muted">
          A free diagnostic for each exam. No card required.
        </p>
      </section>

      <section className="grid gap-5 md:grid-cols-3">
        {[
          {
            title: 'Benchmarked, not guessed',
            body: 'Every question is tagged to an official content domain. Your score is weighted by how much of the real paper each domain actually represents, so a strong result in a 10% domain does not mask a weak one worth 30%.',
          },
          {
            title: 'Speed is a skill too',
            body: 'CAOP gives you about 103 seconds a question. OPRA gives you 75. We track your pace against the real thing from your very first attempt, because running out of time fails just as many candidates as not knowing the content.',
          },
          {
            title: 'Know when you are leaning on your books',
            body: 'CAOP is restricted open-book — but reaching for the AMH on every question will cost you the paper. Flag your lookups as you go and see exactly where your recall is thin.',
          },
        ].map((f) => (
          <div key={f.title} className="card p-4 sm:p-6">
            <h3 className="text-base font-semibold text-ink">{f.title}</h3>
            <p className="mt-2.5 text-sm leading-relaxed text-muted">{f.body}</p>
          </div>
        ))}
      </section>

      {data?.exams.length ? (
        <section>
          <h2 className="text-2xl font-bold tracking-tight text-ink">Both exams, covered properly</h2>
          <div className="mt-6 grid gap-5 md:grid-cols-2">
            {data.exams.map((exam) => (
              <div key={exam.code} className="card p-4 sm:p-6">
                <div className="flex items-baseline gap-2">
                  <h3 className="text-lg font-bold text-ink">{exam.code}</h3>
                  <span className="badge bg-raised text-muted">
                    {exam.openBook ? 'Restricted open-book' : 'Closed-book'}
                  </span>
                </div>
                <p className="mt-1 text-sm font-medium text-muted">{exam.name}</p>
                <p className="mt-3 text-sm leading-relaxed text-muted">{exam.description}</p>

                <dl className="mt-4 grid grid-cols-3 gap-3 border-t border-line pt-4 text-center">
                  <div>
                    <dt className="text-xs text-muted">Questions</dt>
                    <dd className="text-lg font-bold text-ink">{exam.questionCount}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted">Minutes</dt>
                    <dd className="text-lg font-bold text-ink">{exam.timeLimitSec / 60}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted">
                      Sec<span className="sm:hidden">/Q</span>
                      <span className="hidden sm:inline">/question</span>
                    </dt>
                    <dd className="text-lg font-bold text-ink">{exam.benchmarkPaceSec}</dd>
                  </div>
                </dl>

                <ul className="mt-4 space-y-1.5">
                  {exam.domains.map((d) => (
                    <li key={d.code} className="flex items-baseline justify-between gap-3 text-sm">
                      <span className="text-muted">{d.name}</span>
                      <span className="shrink-0 font-semibold tabular-nums text-ink">{d.weightPct}%</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="rounded-xl border border-line bg-surface p-4 sm:p-6">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">Important</h2>
        <p className="mt-2.5 text-sm leading-relaxed text-muted">{INDEPENDENCE_DISCLAIMER}</p>
      </section>
    </div>
  );
}
